import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  LayoutDashboard,
  PanelLeftClose,
  PanelLeftOpen,
  PenLine,
  Sparkles,
} from 'lucide-react';

export type MerlinSessionSummary = {
  id: string;
  title: string;
  updatedAt: number;
};

type Props = {
  children: ReactNode;
  sessions: MerlinSessionSummary[];
  activeSessionId: string;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onNewChat: () => void;
  onSelectSession: (id: string) => void;
};

function formatSessionDate(timestamp: number) {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  if (isToday) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function MerlinShell({
  children,
  sessions,
  activeSessionId,
  sidebarOpen,
  onToggleSidebar,
  onNewChat,
  onSelectSession,
}: Props) {
  const navigate = useNavigate();

  return (
    <div className="flex h-screen overflow-hidden bg-white text-gray-900">
      <aside
        className={`flex shrink-0 flex-col border-r border-gray-200 bg-gray-50 transition-[width] duration-200 ${
          sidebarOpen ? 'w-[260px]' : 'w-0 overflow-hidden border-r-0'
        }`}
      >
        <div className="flex h-14 items-center justify-between px-3">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-200/70"
          >
            <Sparkles className="h-4 w-4" />
            Merlin
          </button>
          <button
            type="button"
            onClick={onToggleSidebar}
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-200/70 hover:text-gray-800"
            aria-label="Close sidebar"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        </div>

        <div className="px-3 pb-2">
          <button
            type="button"
            onClick={onNewChat}
            className="flex w-full items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-800 shadow-sm transition-colors hover:bg-gray-100"
          >
            <PenLine className="h-4 w-4" />
            New chat
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-2">
          <p className="px-2 py-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">
            Chats
          </p>
          {sessions.length === 0 ? (
            <p className="px-2 py-1 text-sm text-gray-400">No chats yet</p>
          ) : (
            <ul className="space-y-0.5">
              {sessions.map((session) => {
                const isActive = session.id === activeSessionId;
                return (
                  <li key={session.id}>
                    <button
                      type="button"
                      onClick={() => onSelectSession(session.id)}
                      className={`group flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                        isActive
                          ? 'bg-gray-200/80 text-gray-900'
                          : 'text-gray-700 hover:bg-gray-200/60'
                      }`}
                    >
                      <span className="truncate">{session.title}</span>
                      <span className="shrink-0 text-[11px] text-gray-400 opacity-0 transition-opacity group-hover:opacity-100">
                        {formatSessionDate(session.updatedAt)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="border-t border-gray-200 p-3">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-200/70 hover:text-gray-900"
          >
            <LayoutDashboard className="h-4 w-4" />
            Back to dashboard
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-gray-100 px-3">
          {!sidebarOpen ? (
            <button
              type="button"
              onClick={onToggleSidebar}
              className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
              aria-label="Open sidebar"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </button>
          ) : null}
          <div className="min-w-0 flex-1 truncate text-sm font-medium text-gray-700">
            {sessions.find((s) => s.id === activeSessionId)?.title ?? 'New chat'}
          </div>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
          >
            <ChevronLeft className="h-4 w-4" />
            Dashboard
          </button>
        </header>

        {children}
      </div>
    </div>
  );
}
